import type { OrientationRpy, Point3D, ResolvedPose } from "../types";

export interface MeasurementRaw {
    id: string;
    deletedAt?: string;
    meta: {
        position: Point3D;
        /** Source-schema orientation in radians. */
        rotation: OrientationRpy;
        telemetry_pose_optimized?: {
            position_xyz: Point3D;
            orientation_rpy: OrientationRpy;
        };
    };
    [key: string]: unknown;
}

export type MeasurementPoseMode = "original" | "telemetry-optimized";

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const isPoint3D = (value: unknown): value is Point3D => {
    if (typeof value !== "object" || value === null) return false;
    if (!("x" in value) || !("y" in value) || !("z" in value)) {
        return false;
    }

    return (
        isFiniteNumber(value.x) &&
        isFiniteNumber(value.y) &&
        isFiniteNumber(value.z)
    );
};

const isOrientationRpy = (value: unknown): value is OrientationRpy => {
    if (typeof value !== "object" || value === null) return false;
    if (!("r" in value) || !("p" in value) || !("y" in value)) {
        return false;
    }

    return (
        isFiniteNumber(value.r) &&
        isFiniteNumber(value.p) &&
        isFiniteNumber(value.y)
    );
};

/**
 * Resolves one complete map pose for the requested display mode. Telemetry
 * optimization is preferred only when its position and RPY orientation are both
 * complete and finite.
 */
export function resolveMeasurementPose(
    measurement: MeasurementRaw,
    mode: MeasurementPoseMode = "telemetry-optimized",
): ResolvedPose | null {
    const measurementMeta = measurement?.meta;
    const telemetryPose = measurementMeta?.telemetry_pose_optimized;
    if (
        mode === "telemetry-optimized" &&
        typeof telemetryPose === "object" &&
        telemetryPose !== null &&
        isPoint3D(telemetryPose.position_xyz) &&
        isOrientationRpy(telemetryPose.orientation_rpy)
    ) {
        return {
            pose: {
                position: telemetryPose.position_xyz,
                orientation: telemetryPose.orientation_rpy,
            },
            source: "telemetry-optimized",
        };
    }

    if (
        isPoint3D(measurementMeta?.position) &&
        isOrientationRpy(measurementMeta?.rotation)
    ) {
        return {
            pose: {
                position: measurementMeta.position,
                orientation: measurementMeta.rotation,
            },
            source: "original",
        };
    }

    return null;
}
